import { Button, Card, CardBody, CardHeader, Text } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { getCapitalActivityEventStateBadge } from "features/capital-activity/CapitalActivityUtils";
import { InvestorTransferBackdatedWarningBanner } from "features/investor-transfer/InvestorTransferBackdatedWarningBanner";
import { InvestorTransferDetailsFormFields } from "features/investor-transfer/InvestorTransferDetailsFormFields";
import { InvestorTransferSetupFooter } from "features/investor-transfer/setup/InvestorTransferSetupFooter";
import {
  getFundFamilyEventRetrieveQueryKey,
  useFundFamilyEventRetrieve,
} from "gen/endpoints/fund-family-event/fund-family-event";
import {
  getInvestorTransferorsForInvestorTransferListQueryKey,
  getInvestorTransferRetrieveQueryKey,
  useInvestorTransferDestroy,
  useInvestorTransferorsForInvestorTransferListSuspense,
  useInvestorTransferRetrieveSuspense,
  useInvestorTransferUpsert,
} from "gen/endpoints/investor-transfer/investor-transfer";
import { getInvestorTransferTableDataRetrieveQueryKey } from "gen/endpoints/investor-transfer-table/investor-transfer-table";
import { useLatestTransactionGeneratingFundFamilyEventRetrieveSuspense } from "gen/endpoints/latest-transaction-generating-fund-family-event/latest-transaction-generating-fund-family-event";
import { InvestorTransferUpsertDataRequest } from "gen/model/investorTransferUpsertDataRequest";
import { investorTransferUpsertBody } from "gen/schemas/investor-transfer/investor-transfer";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Form from "shared/components/form/Form";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { usePageWizard } from "shared/components/layout/PageWizard";
import { useConfirmationDialog } from "shared/components/modal/confirm/ConfirmationDialog";
import { DeleteConfirmationDialog } from "shared/components/modal/DeleteConfirmationDialog";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";
import { useInvalidateQueryKeys } from "shared/utils/QueryUtils";

export const InvestorTransferSetupDetailsPage = () => {
  const { t } = useTranslation(["common", "investorTransfer"]);
  const { fundFamilyId, investorTransferId } = useRequiredStringParams();
  const { navigateNext } = usePageWizard();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: investorTransferData } = useInvestorTransferRetrieveSuspense(
    fundFamilyId,
    investorTransferId,
  );

  const { data: fundFamilyEventData } = useFundFamilyEventRetrieve(
    fundFamilyId,
    investorTransferData.fund_family_event_id,
    {
      query: {
        enabled: !!investorTransferData.fund_family_event_id,
      },
    },
  );

  const { data: investorTransferorDataList } =
    useInvestorTransferorsForInvestorTransferListSuspense(
      fundFamilyId,
      investorTransferId,
    );

  const { mutateAsync: deleteTransfer } = useInvestorTransferDestroy();

  const form = useForm<InvestorTransferUpsertDataRequest>({
    resolver: zodResolver(investorTransferUpsertBody),
    defaultValues: {
      ...investorTransferData,
      transferor_investor_id: investorTransferorDataList[0].investor_id,
      security_ownership_ids: investorTransferorDataList.map(
        (transferor) => transferor.security_ownership_id,
      ),
    },
  });

  const { mutateAsync: upsertInvestorTransfer } = useInvestorTransferUpsert();

  const { data: latestEventData } =
    useLatestTransactionGeneratingFundFamilyEventRetrieveSuspense(
      fundFamilyId,
      {
        fund_family_event_id_to_exclude:
          investorTransferData.fund_family_event_id,
        exclude_fees: true,
      },
    );

  const recordedDate = form.watch("recorded_date");

  const confirmDelete = useConfirmationDialog(
    DeleteConfirmationDialog as DeleteConfirmationDialog,
    {
      title: t("investorTransfer:delete.title"),
      description: t("investorTransfer:delete.description"),
      successMessage: t("investorTransfer:delete.successMessage"),
      performDelete: () =>
        deleteTransfer({
          fundFamilyId,
          investorTransferId,
        }),
    },
  );

  const onClickDelete = async () => {
    const success = await confirmDelete();
    if (success) {
      await queryClient.invalidateQueries({
        queryKey: getInvestorTransferTableDataRetrieveQueryKey(fundFamilyId),
      });
      navigate(
        rootRoute({})
          .fundFamilies({})
          .fundFamily({ fundFamilyId })
          .commitments({})
          .transfersTab({}).$,
      );
    }
  };
  const invalidateQueries = useInvalidateQueryKeys();

  return (
    <PageContent contentWidth="boxed">
      <Form
        form={form}
        onSubmit={async (data) => {
          await upsertInvestorTransfer({
            fundFamilyId,
            data,
          });
          await invalidateQueries(
            getFundFamilyEventRetrieveQueryKey(
              fundFamilyId,
              investorTransferData.fund_family_event_id,
            ),
            getInvestorTransferRetrieveQueryKey(
              fundFamilyId,
              investorTransferId,
            ),
            getInvestorTransferorsForInvestorTransferListQueryKey(
              fundFamilyId,
              investorTransferId,
            ),
          );
        }}
        onSuccess={navigateNext}
      >
        <PageHeader
          size="lg"
          workflowName={t("investorTransfer:setup.superHeading")}
          title={t("investorTransfer:setup.details.title")}
          badge={getCapitalActivityEventStateBadge(
            fundFamilyEventData?.state,
            t,
          )}
          actions={
            <Button colorScheme="destructive" onClick={onClickDelete}>
              {t("common:delete")}
            </Button>
          }
        />
        <PageBody>
          {latestEventData.latest_event_date && recordedDate && (
            <InvestorTransferBackdatedWarningBanner
              recordedDate={recordedDate}
              calculatedDate={latestEventData.latest_event_date}
            />
          )}
          <Card variant="form">
            <CardHeader>
              <Text textStyle="bodyBold">
                {t("investorTransfer:transferDetails")}
              </Text>
            </CardHeader>
            <CardBody>
              <InvestorTransferDetailsFormFields
                fundFamilyId={fundFamilyId}
                form={form}
              />
            </CardBody>
          </Card>
          <InvestorTransferSetupFooter fundFamilyId={fundFamilyId} />
        </PageBody>
      </Form>
    </PageContent>
  );
};
